import React, { useState, useEffect } from 'react';
import { Button, Alert, Card, Typography, Tooltip } from 'antd';
import { useTransformation } from '../../hooks/useTransformation';
import TransformationStatus from './TransformationStatus';

interface DataCollTransformationPanelProps {
  dataCollName: string;
  client: { id: string; token: string; permissions: string[] };
  validationResult: any;
  validationLoading: boolean;
  triggerValidationRefresh: () => Promise<any>;
  style?: React.CSSProperties;
  setShowRecalc: React.Dispatch<React.SetStateAction<boolean>>;
  allDiffsResolved: boolean;
  hasTableDiffs: boolean;
  hasBasisTable: boolean;
  allDiffsApplied: boolean;
  allStructuralMappingsApplied: boolean;
  hasBasisUploadTable: boolean;
  expertModeActive?: boolean;
  hideStructureValidationAlerts?: boolean;
  clearTransformationStatus?: () => void;
  childClearTransformationStatusRef?: React.MutableRefObject<(() => void) | null>;
}

const DataCollTransformationPanel: React.FC<DataCollTransformationPanelProps> = ({
  dataCollName,
  client,
  validationResult,
  validationLoading,
  triggerValidationRefresh,
  style,
  setShowRecalc,
  allDiffsResolved,
  hasTableDiffs,
  hasBasisTable,
  allDiffsApplied,
  allStructuralMappingsApplied,
  hasBasisUploadTable,
  expertModeActive,
  hideStructureValidationAlerts = false,
  clearTransformationStatus,
  childClearTransformationStatusRef,
}) => {
  const {
    loading,
    legacyLoading,
    transformationTriggered,
    transformationTriggeredAt,
    handleGenerate,
    handleGenerateLegacyDataColl,
    clearTransformationStatus: clearLocalTransformationStatus,
  } = useTransformation({
    dataCollName,
    client,
    triggerValidationRefresh,
    setShowRecalc,
  });

  const [showValidationFailedAlert, setShowValidationFailedAlert] = useState(false);
  const [showValidationWarningsAlert, setShowValidationWarningsAlert] = useState(false);

  // Register the local clear function with the parent
  useEffect(() => {
    if (childClearTransformationStatusRef) {
      childClearTransformationStatusRef.current = clearLocalTransformationStatus;
    }
  }, [childClearTransformationStatusRef, clearLocalTransformationStatus]);

  useEffect(() => {
    setShowValidationFailedAlert(validationResult?.valid === false);
    setShowValidationWarningsAlert(validationResult?.warnings?.length > 0);
    setShowRecalc(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCollName, client, validationResult]);

  const handleGenerateWithValidation = async () => {
    setShowValidationFailedAlert(false);

    // Validate first if in expert mode
    if (triggerValidationRefresh) {
      try {
        const result = await triggerValidationRefresh();
        if (!result?.valid) {
          setShowValidationFailedAlert(true);
          return;
        }
      } catch (error) {
        setShowValidationFailedAlert(true);
        return;
      }
    }

    // Trigger transformation
    try {
      await handleGenerate();
    } catch (error: any) {
      // Error already handled in handleGenerate
    }
  };

  const handleGenerateLegacyWithValidation = async () => {
    setShowValidationFailedAlert(false);
    try {
      await handleGenerateLegacyDataColl();
    } catch (error: any) {
      // Error already handled in handleGenerateLegacyDataColl
    }
  };

  return (
    <Card
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          Stammdatentabelle verarbeiten
        </Typography.Title>
      }
      style={{ marginBottom: 12, ...style }}
    >
      {showValidationFailedAlert && (
        <Alert
          type="error"
          showIcon
          message={
            <span>
              Validierung fehlgeschlagen. Bitte beheben Sie die{' '}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                onClick={() => {
                  const el = document.getElementById('basis-validation-section');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                oben angezeigten Fehler
              </Button>
              , und versuchen es dann erneut.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {showValidationWarningsAlert && (
        <Alert
          type="warning"
          showIcon
          message={
            <span>
              Es liegen Warnungen in der Validierung vor. Sie können die Stammdatentabelle trotzdem
              verarbeiten. Wir empfehlen Ihnen,{' '}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                onClick={() => {
                  const el = document.getElementById('basis-validation-section');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                diese Warnungen
              </Button>{' '}
              zuerst zu beheben.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {!hideStructureValidationAlerts &&
        hasBasisTable &&
        hasBasisUploadTable &&
        !allStructuralMappingsApplied && (
          <>
            {expertModeActive ? (
              <Alert
                type="error"
                showIcon
                message={
                  <span>
                    Die Tabellenstruktur stimmt nicht mit der vorhandenen Stammdatentabelle überein.
                    Bitte überprüfen Sie im{' '}
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                      onClick={() => {
                        const el = document.getElementById('structural-mappings-section');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      strukturellen Abgleich
                    </Button>{' '}
                    die Unterschiede der Tabellenstruktur, lösen Sie die Unterschiede auf und wenden
                    Sie den Abgleich an, bevor Sie mit der Verarbeitung der Stammdatentabelle
                    fortfahren.
                    <br />
                    Alternativ können Sie die hochgeladene Stammdatentabelle löschen, erneut
                    herunterladen, lediglich die Daten bearbeiten und die Stammdatentabelle
                    anschließend erneut hochladen.
                  </span>
                }
                style={{ marginBottom: 12 }}
              />
            ) : (
              <Alert
                type="error"
                showIcon
                message={
                  <span>
                    Die neue Tabellenstruktur stimmt nicht mit der vorhandenen Stammdatentabelle
                    überein. Bitte überprüfen Sie die Tabellenstruktur und laden Sie die
                    Stammdatentabelle erneut hoch. Alternativ kann ein Administrator Ihnen helfen
                    die Strukturellen Unterschiede aufzulösen.
                  </span>
                }
                style={{ marginBottom: 12 }}
              />
            )}
          </>
        )}
      {hasTableDiffs && !allDiffsResolved && (
        <Alert
          type="warning"
          showIcon
          message={
            <span>
              Es liegen ungelöste Differenzen im{' '}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                onClick={() => {
                  const el = document.getElementById('data-merge-section');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                inhaltlichen Abgleich
              </Button>{' '}
              vor. Die hochgeladene Stammdatentabelle hat also unerwartete Abweichungen zu den
              Basisdaten, welche aktuell verwendet werden.
              <br /> Wir empfehlen diese zuerst zu überprüfen und als gelöst zu markieren, bevor Sie
              mit der Verarbeitung der Stammdatentabelle fortfahren.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {hasTableDiffs && !allDiffsApplied && (
        <Alert
          type="warning"
          showIcon
          message={
            <span>
              Es liegen nicht angewendete Änderungen im{' '}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                onClick={() => {
                  const el = document.getElementById('data-merge-section');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                inhaltlichen Abgleich
              </Button>{' '}
              vor. Bitte wenden Sie diese Änderungen an, bevor Sie mit der Verarbeitung der
              Stammdatentabelle fortfahren. Ansonsten sind Ihre anpassungen im Inhaltliche Abgleich
              unwirksam. Sie können ihre wirksamen Änderungen in dem Abschnitt "Stammdatentabelle
              Uploadbereich" einsehen.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {hasBasisUploadTable && (
        <Tooltip title="Die hochgeladene Stammdatentabelle verarbeiten.">
          <Button
            type={hasTableDiffs && allDiffsResolved ? 'primary' : 'default'}
            danger={hasTableDiffs && !allDiffsResolved}
            onClick={handleGenerateWithValidation}
            loading={loading}
            disabled={
              !validationResult?.valid ||
              (hasBasisTable && !hideStructureValidationAlerts && !allStructuralMappingsApplied) ||
              loading
            }
          >
            Stammdatentabelle verarbeiten
          </Button>
        </Tooltip>
      )}
      {!hideStructureValidationAlerts &&
        hasBasisUploadTable &&
        expertModeActive &&
        ((validationResult && !validationResult.valid) ||
          (hasBasisTable && !allStructuralMappingsApplied) ||
          (hasBasisTable && !allDiffsResolved)) && (
          <Tooltip title="Die hochgeladene Stammdatentabelle trotz Fehlern verarbeiten.">
            <Button
              type={'default'}
              danger={true}
              onClick={handleGenerateWithValidation}
              loading={loading}
              disabled={loading}
              style={{ marginLeft: 12 }}
            >
              Stammdatentabelle trotz Fehlern verarbeiten
            </Button>
          </Tooltip>
        )}
      {
        hasBasisTable && (
          <Tooltip
            title={`Verarbeitung der vorigen Stammdatentabelle (${dataCollName}_basis) wiederherstellen.`}
          >
            <Button
              type="default"
              onClick={handleGenerateLegacyWithValidation}
              loading={legacyLoading}
              style={{ marginLeft: hasBasisUploadTable ? 12 : 0 }}
            >
              Verarbeitung der vorigen Stammdatentabelle wiederherstellen
            </Button>
          </Tooltip>
        ) // TODO: Else maybe show a warning that the basis table is not available, so restore only through data_coll_backup possible not through generating it again
      }
      <TransformationStatus
        dataCollName={dataCollName}
        client={client}
        transformationTriggered={transformationTriggered}
        transformationTriggeredAt={transformationTriggeredAt}
        hasBasisUploadTable={hasBasisUploadTable}
        style={{ marginTop: 12 }}
      />
    </Card>
  );
};

export default DataCollTransformationPanel;
